/**
 * Video Generation Progress Component
 * Minimal horizontal stepper for video generation workflow
 * Shows 3 phases: Synchronizing → Assembling → Setup
 * Uses time-based linear progress estimation for smooth UX
 */

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { CheckCircle2, Clock, Layers, Settings, Loader2 } from 'lucide-react';
import { VideoGenerationStatus } from '@/types/workflow';
import { useAgentJob } from '@/hooks/useAgentJob';

interface VideoGenerationProgressProps {
  videoGenerationStatus: VideoGenerationStatus;
}

/**
 * Estimated durations for each phase (in seconds)
 */
const ESTIMATED_DURATIONS = {
  map_timing: 30,      // 30 seconds for scene timing
  assembly: 20,        // 20 seconds for manifest assembly
  setup: 0,            // instant
} as const;

/**
 * Creator-friendly phase configuration
 */
const PHASE_CONFIG = {
  map_timing: {
    icon: Clock,
    label: 'Synchronizing Scenes',
    shortLabel: 'Synchronizing',
  },
  assembly: {
    icon: Layers,
    label: 'Assembling Video',
    shortLabel: 'Assembling',
  },
  setup: {
    icon: Settings,
    label: 'Finalizing',
    shortLabel: 'Setup',
  },
} as const;

/**
 * Horizontal phase indicator with animated gradient line
 */
interface PhaseNodeProps {
  phase: keyof typeof PHASE_CONFIG;
  currentPhase: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
  isLast?: boolean;
  lineProgress?: number; // 0-100, progress for this connecting line
}

function PhaseNode({ phase, currentPhase, status, isLast, lineProgress = 0 }: PhaseNodeProps) {
  const config = PHASE_CONFIG[phase];
  const Icon = config.icon;
  const isActive = currentPhase === phase;
  const isCompleted = status === 'completed';
  const isPending = status === 'pending';

  return (
    <div className="flex items-center flex-1">
      {/* Phase node */}
      <div className="flex flex-col items-center gap-1.5">
        {/* Icon circle */}
        <div
          className={cn(
            'relative w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300',
            isCompleted && 'bg-green-100 border-green-500 dark:bg-green-900/30 dark:border-green-500',
            isActive && 'bg-accent border-primary',
            isPending && 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600'
          )}
        >
          {isCompleted ? (
            <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
          ) : (
            <Icon
              className={cn(
                'w-4 h-4 transition-opacity duration-300',
                isActive && 'text-primary dark:text-accent-foreground',
                isPending && 'text-gray-400 dark:text-gray-500'
              )}
            />
          )}
        </div>

        {/* Label */}
        <span
          className={cn(
            'text-xs font-medium transition-opacity duration-300',
            isActive && 'text-primary dark:text-accent-foreground',
            isCompleted && 'text-green-600 dark:text-green-400',
            isPending && 'text-gray-500 dark:text-gray-500'
          )}
        >
          {config.shortLabel}
        </span>
      </div>

      {/* Connecting line with animated gradient */}
      {!isLast && (
        <div className="flex-1 h-1.5 mx-2 mt-[-18px] relative overflow-hidden rounded-full">
          {/* Background line */}
          <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700" />

          {/* Progress overlay - blue gradient when in progress, green when completed */}
          {lineProgress > 0 && (
            <div
              className={cn(
                'absolute inset-0 transition-all duration-500 ease-out',
                lineProgress >= 100
                  ? 'bg-green-500 dark:bg-green-500'
                  : 'bg-primary'
              )}
              style={{
                transform: `scaleX(${lineProgress / 100})`,
                transformOrigin: 'left',
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Main component - Minimal horizontal stepper with animated gradient progress
 */
export function VideoGenerationProgress({ videoGenerationStatus }: VideoGenerationProgressProps) {
  const { currentSubstep, substeps } = videoGenerationStatus;

  // Time tracking state for each phase
  const [timingStartTime, setTimingStartTime] = useState<number | null>(null);
  const [assemblyStartTime, setAssemblyStartTime] = useState<number | null>(null);

  if (!substeps) {
    return (
      <div className="text-sm text-gray-600 dark:text-gray-400">
        Initializing video generation workflow...
      </div>
    );
  }

  // Get current phase
  const currentPhase = currentSubstep || 'map_timing';

  // Get active job ID for thinking display (only for assembly phase)
  const activeJobId =
    substeps.assembly.status === 'in_progress'
      ? substeps.assembly.jobId
      : undefined;

  // Use agent job hook to get thinking text (for assembly phase)
  const { job } = useAgentJob(activeJobId);

  // Track start time when phase becomes active
  useEffect(() => {
    if (substeps.mapTiming.status === 'in_progress' && !timingStartTime) {
      setTimingStartTime(Date.now());
    }
  }, [substeps.mapTiming.status, timingStartTime]);

  useEffect(() => {
    if (substeps.assembly.status === 'in_progress' && !assemblyStartTime) {
      setAssemblyStartTime(Date.now());
    }
  }, [substeps.assembly.status, assemblyStartTime]);

  // Trigger re-render every second to update progress smoothly
  const [, setTick] = useState(0);
  useEffect(() => {
    if (substeps.mapTiming.status === 'in_progress' || substeps.assembly.status === 'in_progress') {
      const interval = setInterval(() => {
        setTick((prev) => prev + 1);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [substeps.mapTiming.status, substeps.assembly.status]);

  // Calculate current phase progress (0-100, capped at 95 until confirmed)
  const getCurrentPhaseProgress = (): number => {
    if (currentPhase === 'map_timing' && timingStartTime) {
      const elapsed = Math.floor((Date.now() - timingStartTime) / 1000);
      const estimated = ESTIMATED_DURATIONS.map_timing;
      return Math.min(95, (elapsed / estimated) * 100);
    }
    if (currentPhase === 'assembly' && assemblyStartTime) {
      const elapsed = Math.floor((Date.now() - assemblyStartTime) / 1000);
      const estimated = ESTIMATED_DURATIONS.assembly;
      return Math.min(95, (elapsed / estimated) * 100);
    }
    return 0;
  };

  // Calculate line progress for each connecting line
  // Line 1: Fills during map_timing phase
  const line1Progress =
    substeps.mapTiming.status === 'completed'
      ? 100
      : currentPhase === 'map_timing'
      ? getCurrentPhaseProgress()
      : 0;

  // Line 2: Fills during assembly phase
  const line2Progress =
    substeps.assembly.status === 'completed'
      ? 100
      : currentPhase === 'assembly'
      ? getCurrentPhaseProgress()
      : 0;

  // Check if all complete
  const isComplete = substeps.setup.status === 'completed';

  // Get thinking text (only for assembly phase)
  const thinkingText = job?.progress?.thinking;

  // Build status messages for each phase
  const getPhaseMessage = () => {
    if (substeps.mapTiming.status === 'in_progress') {
      return 'Mapping scene timing data and generating word-level timestamps...';
    }
    if (substeps.assembly.status === 'in_progress') {
      if (thinkingText) {
        return thinkingText;
      }
      return 'Assembling manifest and selecting transitions...';
    }
    if (substeps.setup.status === 'in_progress') {
      return 'Finalizing video project and updating state...';
    }
    return null;
  };

  const phaseMessage = getPhaseMessage();

  // Build result summary text
  const getResultSummary = () => {
    if (substeps.mapTiming.status === 'completed' && substeps.mapTiming.result) {
      const { audioDuration, mappedScenes, unmappedScenes } = substeps.mapTiming.result;
      const unmappedCount = unmappedScenes?.length || 0;

      return (
        <div className="text-xs text-green-600 dark:text-green-400">
          ✓ Mapped {mappedScenes} scene{mappedScenes !== 1 ? 's' : ''}, audio duration: {audioDuration?.toFixed(1)}s
          {unmappedCount > 0 && ` (${unmappedCount} unmapped)`}
        </div>
      );
    }

    if (substeps.assembly.status === 'completed' && substeps.assembly.result) {
      const { slideCount, totalDuration } = substeps.assembly.result;

      return (
        <div className="text-xs text-green-600 dark:text-green-400">
          ✓ Manifest generated: {slideCount} slides, {totalDuration?.toFixed(1)}s total duration
        </div>
      );
    }

    return null;
  };

  const resultSummary = getResultSummary();

  return (
    <div className="space-y-2 p-3">
      {/* Header with status message */}
      <div className="space-y-1">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Generating Final Video
        </h4>

        {/* Phase message display */}
        {phaseMessage && (
          <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
            <Loader2 className="h-3 w-3 animate-spin flex-shrink-0 mt-0.5" />
            <span>{phaseMessage}</span>
          </div>
        )}

        {/* Result summary */}
        {resultSummary}
      </div>

      {/* Horizontal Stepper with animated gradient lines */}
      <div className="flex items-center pt-1">
        <PhaseNode
          phase="map_timing"
          currentPhase={currentPhase}
          status={substeps.mapTiming.status}
          lineProgress={line1Progress}
        />
        <PhaseNode
          phase="assembly"
          currentPhase={currentPhase}
          status={substeps.assembly.status}
          lineProgress={line2Progress}
        />
        <PhaseNode
          phase="setup"
          currentPhase={currentPhase}
          status={substeps.setup.status}
          isLast
        />
      </div>

      {/* Completion message */}
      {isComplete && (
        <div className="flex items-center gap-2 text-green-600 dark:text-green-400 pt-1">
          <CheckCircle2 className="h-4 w-4" />
          <span className="text-xs font-medium">Video generated successfully!</span>
        </div>
      )}
    </div>
  );
}
